import { DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { proposalrequest } from '@mep/pae-commons';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';

import EntityMenu from '@/components/EntityMenu';
import useAlerts from '@/hooks/useAlerts';
import useCrud, { showConfirm } from '@/hooks/useCrud';
import { usePermissions } from '@/hooks/usePermissions';
import {
  CACHE_KEY_LIST_PROPOSAL_REQUEST,
  ROUTE_PATH_PROPOSAL_REQUEST,
  SECURITY_KEY_PROPOSAL_REQUEST
} from '@/modules/proposalRequest/contants/proposalRequest';
import ProposalRequestApiService from '@/modules/proposalRequest/services/ProposalRequestApiService';

const { ACCEPTED, REJECTED } = proposalrequest;

const options = [
  {
    icon: <EyeOutlined/>,
    title: 'details',
    action: 'details',
  },
  {
    icon: <EditOutlined/>,
    title: 'edit',
    action: 'edit',
    style: { minWidth: 160 }
  },
  {
    icon: <DownloadOutlined/>,
    title: 'downloadPDF.title',
    action: 'downloadPDF'
  },
  {
    title: 'remove',
    action: 'remove',
    divider: true,
  },
  {
    icon: <DeleteOutlined/>,
    title: 'remove',
    action: 'remove',
  }
];
const translate = 'proposalRequest';

const EntityRowActions = ({ entity, canEdit, canRemove, entityName }) => {
  const { push } = useHistory();
  const { t } = useTranslation(translate);
  const { putErrorCode } = useAlerts();
  const { remove, isLoading } = useCrud(ProposalRequestApiService, CACHE_KEY_LIST_PROPOSAL_REQUEST);
  const { mutateAsync } = useMutation(ProposalRequestApiService.downloadPDFCertificationAval);
  const { canWrite, canDelete } = usePermissions(entityName);

  const handleClick = useCallback(({ action }) => {
    switch (action) {
      case 'details': {
        push(`${ROUTE_PATH_PROPOSAL_REQUEST}/${entity?._id}`);
        break;
      }
      case 'edit': {
        push(`${ROUTE_PATH_PROPOSAL_REQUEST}/${entity?._id}/edit`);
        break;
      }

      case 'remove': {
        showConfirm(t, entity['name'], () => remove(entity._id));
        break;
      }
      case 'downloadPDF': {
        showConfirm(t, entity['name'], async () => {
          try {
            await mutateAsync({
              _id: entity._id,
              pdfName: `${entity?.code}-${moment().format('DD-MM-YYYY').toString()}.pdf`
            });
          } catch (e) {
            putErrorCode(e);
          }
        }, 'downloadPDF.question');
        break;
      }
      default: {
        console.log('missing action handler');
      }
    }
  }, [push, entity, t, remove, mutateAsync, putErrorCode]);

  const menuOptions = useMemo(() => options.filter(({ action }) => {
    switch (action) {
      case 'edit':
        return ![ACCEPTED, REJECTED].includes(entity?.status) && canWrite;
      case 'remove':
        return canDelete;
      default:
        return true;
    }
  }), [entity, canWrite, canDelete]);

  return (
    <EntityMenu
      disabled={!menuOptions || !menuOptions.length}
      options={menuOptions}
      hide={{
        edit: !canEdit,
        remove: !canRemove
      }}
      translate={translate}
      onClick={handleClick} isLoading={isLoading}/>
  );

};

/**
 * cellRender
 * */
/**
 * This is not a component,
 * it is a function to render the Status component in a table cell
 * @param {String} text this is the value of the dataIndex of the table (we will ignored in this case)
 * @param {Object} user this is the row instance
 * */

EntityRowActions.propTypes = {
  canEdit: PropTypes.bool,
  canRemove: PropTypes.bool,
  entity: PropTypes.object,
  entityName: PropTypes.any
};
EntityRowActions.defaultProps = {
  canEdit: true,
  canRemove: true
};

export default memo(EntityRowActions);

export const renderProposalRequestActions = (id, entity) => {
  return (
    <EntityRowActions entity={entity} entityName={SECURITY_KEY_PROPOSAL_REQUEST}/>
  );
};

